import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Search, X, Check, BadgeCheck, BellRing, Download } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { getFans, getFanDetail, getAnalyticsDimensions, exportFansCsv, triggerDownload } from "./adminApi";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { year: "2-digit", month: "short", day: "numeric" }) : "—");

export default function Fans() {
  const { slug } = useParams();
  const [dims, setDims] = useState({ platforms: [], triggers: [], content_pieces: [] });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ platform: "", trigger: "", content_piece: "", returned: "", keep_me_in: "", date_from: "", date_to: "", q: "" });
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  useEffect(() => { getAnalyticsDimensions(slug).then(setDims).catch(() => {}); }, [slug]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== ""));
      setData(await getFans(slug, params));
    } catch (e) { toast.error("Could not load fans"); }
    finally { setLoading(false); }
  }, [slug, f]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const openFan = async (fanId) => {
    setDetailOpen(true); setDetail(null);
    try { setDetail(await getFanDetail(slug, fanId)); }
    catch (e) { toast.error("Could not load fan"); }
  };

  const trigLabel = (id) => (dims.triggers.find((t) => t.id === id)?.label) || id || "—";

  const [exporting, setExporting] = useState(false);
  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== ""));
      const blob = await exportFansCsv(slug, params);
      triggerDownload(blob, `fanly-${slug}-fans.csv`);
      toast.success("CSV downloaded");
    } catch (e) { toast.error("Export failed"); }
    finally { setExporting(false); }
  };

  return (
    <div data-testid="fans-screen" className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-3xl font-bold tracking-tight">Fans</h2>
          <p className="text-[#8a8a8a] text-sm mt-1">Everyone who unlocked this release. Attribution shown is their original acquisition source.</p>
        </div>
        <button data-testid="export-fans-csv" onClick={exportCsv} disabled={exporting}
          className="flex items-center gap-2 border border-white/20 rounded-full px-4 py-2 text-sm hover:border-[#FF2A2A] transition-colors disabled:opacity-60 shrink-0">
          {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Export CSV
        </button>
      </div>

      {/* filters */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="col-span-2 md:col-span-1 relative">
          <Search className="w-4 h-4 text-[#555] absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="fan-search" placeholder="Search email / name" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })}
            className="w-full bg-[#0e0e0e] border border-white/15 rounded-lg pl-9 pr-3 py-2 text-sm text-white" />
        </div>
        <select data-testid="fan-filter-platform" value={f.platform} onChange={(e) => setF({ ...f, platform: e.target.value })}
          className="bg-[#0e0e0e] border border-white/15 rounded-lg px-2 py-2 text-sm text-white">
          <option value="">All platforms</option>
          {dims.platforms.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <select data-testid="fan-filter-trigger" value={f.trigger} onChange={(e) => setF({ ...f, trigger: e.target.value })}
          className="bg-[#0e0e0e] border border-white/15 rounded-lg px-2 py-2 text-sm text-white">
          <option value="">All triggers</option>
          {dims.triggers.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
        <select data-testid="fan-filter-content" value={f.content_piece} onChange={(e) => setF({ ...f, content_piece: e.target.value })}
          className="bg-[#0e0e0e] border border-white/15 rounded-lg px-2 py-2 text-sm text-white">
          <option value="">All content</option>
          {dims.content_pieces.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select data-testid="fan-filter-returned" value={f.returned} onChange={(e) => setF({ ...f, returned: e.target.value })}
          className="bg-[#0e0e0e] border border-white/15 rounded-lg px-2 py-2 text-sm text-white">
          <option value="">Returned: any</option>
          <option value="true">Returned</option>
          <option value="false">Not returned</option>
        </select>
        <select data-testid="fan-filter-kmi" value={f.keep_me_in} onChange={(e) => setF({ ...f, keep_me_in: e.target.value })}
          className="bg-[#0e0e0e] border border-white/15 rounded-lg px-2 py-2 text-sm text-white">
          <option value="">Keep Me In: any</option>
          <option value="true">Keep Me In</option>
          <option value="false">Not opted</option>
        </select>
        <div className="flex items-end gap-2" data-testid="fan-date-range">
          <label className="flex flex-col gap-1">
            <span className="font-mono-x text-[9px] tracking-widest uppercase text-[#737373]">First unlock — from</span>
            <input data-testid="fan-date-from" type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })}
              className="bg-[#0e0e0e] border border-white/15 rounded-lg px-2 py-2 text-sm text-white" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="font-mono-x text-[9px] tracking-widest uppercase text-[#737373]">First unlock — to</span>
            <input data-testid="fan-date-to" type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })}
              className="bg-[#0e0e0e] border border-white/15 rounded-lg px-2 py-2 text-sm text-white" />
          </label>
          {(f.date_from || f.date_to) && (
            <button data-testid="fan-date-clear" onClick={() => setF({ ...f, date_from: "", date_to: "" })}
              className="mb-0.5 font-mono-x text-[10px] tracking-widest uppercase px-3 py-2 rounded-lg border border-white/15 text-[#a3a3a3] hover:text-white hover:border-white transition-colors">
              Clear
            </button>
          )}
        </div>
      </div>

      {loading || !data ? (
        <div className="flex items-center gap-2 text-[#737373]"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : (
        <>
          <p className="font-mono-x text-[11px] tracking-widest uppercase text-[#737373]" data-testid="fans-count">{data.count} fans</p>
          <div className="overflow-x-auto rounded-2xl border border-white/10">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left font-mono-x text-[10px] tracking-widest uppercase text-[#737373] border-b border-white/10">
                  <th className="px-4 py-3">Email</th><th className="px-4 py-3">Platform</th>
                  <th className="px-4 py-3">Content</th><th className="px-4 py-3">First unlock</th>
                  <th className="px-4 py-3">Last visit</th><th className="px-4 py-3">Listen %</th>
                  <th className="px-4 py-3">Ret.</th><th className="px-4 py-3">KMI</th><th className="px-4 py-3">Consent</th>
                </tr>
              </thead>
              <tbody>
                {data.fans.length === 0 ? (
                  <tr><td colSpan={9} className="px-4 py-8 text-[#737373]">No fans match these filters.</td></tr>
                ) : data.fans.map((fan) => (
                  <tr key={fan.fan_id} data-testid={`fan-row-${fan.fan_id}`} onClick={() => openFan(fan.fan_id)}
                    className="border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors">
                    <td className="px-4 py-3 font-medium max-w-[220px] truncate">
                      {fan.email || <span className="text-[#666]">no email</span>}
                      {fan.unsubscribed && <span className="ml-2 text-[10px] text-[#FF6a6a]">unsub</span>}
                    </td>
                    <td className="px-4 py-3 text-[#cfcfcf]">{fan.platform || "direct"}</td>
                    <td className="px-4 py-3 text-[#cfcfcf]">{fan.content_piece || "—"}</td>
                    <td className="px-4 py-3 text-[#8a8a8a]">{fmtDate(fan.first_unlock)}</td>
                    <td className="px-4 py-3 text-[#8a8a8a]">{fmtDate(fan.last_visit)}</td>
                    <td className="px-4 py-3">{fan.listening_pct}%{fan.complete_listen && <Check className="inline w-3.5 h-3.5 ml-1 text-emerald-400" />}</td>
                    <td className="px-4 py-3">{fan.returned ? <BadgeCheck className="w-4 h-4 text-emerald-400" /> : "—"}</td>
                    <td className="px-4 py-3">{fan.notify_next_drop ? <BellRing className="w-4 h-4 text-[#FF2A2A]" /> : "—"}</td>
                    <td className="px-4 py-3">{fan.consent ? <Check className="w-4 h-4 text-emerald-400" /> : <X className="w-4 h-4 text-[#FF6a6a]" />}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* fan detail */}
      <Sheet open={detailOpen} onOpenChange={setDetailOpen}>
        <SheetContent side="right" className="bg-[#0a0a0c] border-white/10 text-white w-full sm:max-w-md overflow-y-auto" data-testid="fan-detail">
          <SheetHeader><SheetTitle className="font-display text-xl">Fan detail</SheetTitle>
            <SheetDescription className="text-[#737373]">Acquisition, engagement and activity timeline for this fan.</SheetDescription>
          </SheetHeader>
          {!detail ? (
            <div className="flex items-center gap-2 text-[#737373] mt-8"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
          ) : (
            <div className="mt-6 space-y-6">
              <div>
                <p className="font-display text-lg font-bold">{detail.fan.email || "no email"}</p>
                {detail.fan.first_name && <p className="text-[#8a8a8a] text-sm">{detail.fan.first_name}</p>}
              </div>
              <div className="grid grid-cols-3 gap-3">
                {[["Unlocks", detail.summary.unlocks], ["Listens", detail.summary.listens], ["Completes", detail.summary.completes],
                  ["Returns", detail.summary.returns], ["Listen %", `${detail.summary.listening_pct}%`], ["Days active", detail.relationship.days_active]].map(([k, v]) => (
                  <div key={k} className="rounded-xl border border-white/10 p-3">
                    <p className="font-mono-x text-[9px] tracking-widest uppercase text-[#737373]">{k}</p>
                    <p className="font-display text-xl font-bold mt-1">{v}</p>
                  </div>
                ))}
              </div>
              <div className="rounded-xl border border-white/10 p-4 space-y-1 text-sm">
                <p className="font-mono-x text-[10px] tracking-widest uppercase text-[#737373] mb-2">Original acquisition</p>
                <p>Platform: <span className="text-[#cfcfcf]">{detail.acquisition.platform || "direct"}</span></p>
                <p>Content: <span className="text-[#cfcfcf]">{detail.acquisition.content_piece || "—"}</span></p>
                <p>Trigger: <span className="text-[#cfcfcf]">{trigLabel(detail.acquisition.trigger_id)}</span></p>
                <p className="pt-2">Consent: {detail.relationship.consent ? "yes" : "no"} · KMI: {detail.relationship.notify_next_drop ? "yes" : "no"} · Unsub: {detail.relationship.unsubscribed ? "yes" : "no"}</p>
              </div>
              <div>
                <p className="font-mono-x text-[10px] tracking-widest uppercase text-[#737373] mb-2">Timeline</p>
                <div className="space-y-2">
                  {detail.timeline.map((t, i) => (
                    <div key={i} className="flex items-center justify-between text-xs border-b border-white/5 pb-2">
                      <span className="text-[#cfcfcf]">{t.type}</span>
                      <span className="text-[#666]">{fmtDate(t.created_at)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
